// @flow

import React from 'react';
import type { Node } from 'react';
import styled from 'styled-components';
import { flex, p13, Width } from 'styles';

const Wrapper = styled.section`
  ${flex('flex-start', 'center')};
  flex-direction: column;
  margin-top: 75px;

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    margin-top: 64px;
  }

  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    margin-top: 110px;
    flex-direction: row;

    > div {
      &:nth-of-type(1) {
        width: 350px;
        flex: 0 0 auto;
        margin-right: 40px;
      }
    }
  }
`;

const Buttons = styled.div`
  margin-bottom: 24px;
  margin-top: 36px;
  display: ${({ showMobile, buttonBelowOnMobile }) => {
    if (!buttonBelowOnMobile) return 'flex';
    return showMobile ? 'inline-flex' : 'none';
  }};

  > div {
    margin: 0 auto;
  }

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    display: ${({ showMobile, buttonBelowOnMobile }) => (buttonBelowOnMobile ? 'flex' : showMobile ? 'none' : 'flex')};
  }

  @media print, screen and (min-width: ${Width.BREAK_XL}px) {
    > div {
      margin: unset;
    }
  }
`;

const Text = styled.div`
  margin: 8px auto 0 auto;

  * {
    text-align: center;

    @media print, screen and (min-width: ${Width.BREAK_XL}px) {
      text-align: left;
    }
  }

  h2,
  h3 {
    margin-bottom: 24px;
  }
`;

const Table = styled.div`
  position: relative;
  width: 100%;
  max-width: 950px;
  margin: 0 auto 24px auto;
`;

const TableCta = styled.div`
  display: ${({ showMobile, buttonBelowOnMobile }) => {
    if (!buttonBelowOnMobile) return 'flex';
    return showMobile ? 'inline-flex' : 'none';
  }};

  @media print, screen and (min-width: ${Width.BREAK_MD}px) {
    display: ${({ showMobile, buttonBelowOnMobile }) => (buttonBelowOnMobile ? 'flex' : showMobile ? 'none' : 'flex')};
    position: absolute;
    right: 0;
    bottom: 0;
  }

  * {
    margin: 0;
    ${p13};

    a {
      font-weight: 700;
      text-decoration: underline;
    }
  }
`;

type Props = {
  children: Node,
  buttons: Node,
  text: Node,
  tableCta?: Node,
  buttonBelowOnMobile?: boolean,
};

const CtaTable = ({ children, buttons, text, tableCta, buttonBelowOnMobile = false, ...rest }: Props) => (
  <Wrapper {...rest}>
    <div>
      <Text>{text}</Text>
      <Buttons buttonBelowOnMobile={buttonBelowOnMobile}>{buttons}</Buttons>
    </div>
    <Table>
      {children}
      {tableCta && <TableCta buttonBelowOnMobile={buttonBelowOnMobile}>{tableCta}</TableCta>}
    </Table>
    {buttonBelowOnMobile && (
      <>
        <Buttons showMobile>{buttons}</Buttons>
        {tableCta && <TableCta showMobile>{tableCta}</TableCta>}
      </>
    )}
  </Wrapper>
);

export default CtaTable;
